import React, { useState } from "react";
import { Button } from "./ui/button";
import { ExternalLink, User, ShoppingBag, Instagram, Menu, X, Star } from "lucide-react";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 border-b border-cyan-500/30 relative z-50 shadow-lg shadow-purple-500/20">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="flex items-center">
              <span className="text-lg sm:text-xl font-bold">
                <span className="text-cyan-400">maler</span>
                <span className="text-purple-400">bw</span>
                <span className="text-white">festpreis</span>
                <span className="text-xs sm:text-sm text-cyan-400">.de</span>
              </span>
              <div className="ml-2 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 rounded transform -rotate-12 flex items-center justify-center shadow-lg shadow-cyan-500/30 animate-pulse">
                <div className="w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-sm opacity-90"></div>
              </div>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-8">
              <a 
                href="https://www.instagram.com/floriamalerfestpreis/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-pink-400 px-3 py-2 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20 rounded-lg flex items-center gap-2 group"
              >
                <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                <span>Instagram</span>
              </a>
              <a href="#kontakt" className="text-gray-300 hover:text-green-400 px-3 py-2 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 rounded-lg">
                📞 Kontakt
              </a>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-300 hover:text-white p-2 rounded-lg transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Desktop Follow Me Links */}
          <div className="hidden lg:flex items-center gap-4">
            {/* CHECK24 Profi Badge */}
            <a
              href="https://handwerk.check24.de/craftsmen/cs/profile/sp_oqNze"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform duration-300 hover:scale-105"
              title="zum CHECK24 Profi Profil"
            >
              <img 
                src="https://handwerk.check24.de/static/profi_widgets/2018.svg" 
                alt="CHECK24 Profi Siegel" 
                width="96" 
                height="83"
                className="h-12 w-auto" 
              />
            </a>

            {/* Kleinanzeigen Button */}
            <a
              href="https://www.kleinanzeigen.de/pro/maler-mito-aldingen?utm_source=mail&utm_medium=social&utm_campaign=socialbuttons&utm_content=desktop"
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <Button className="bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 hover:from-cyan-600 hover:via-purple-600 hover:to-pink-600 text-white font-semibold px-6 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 shadow-xl hover:shadow-cyan-500/30 transform hover:scale-105 border border-cyan-300/20">
                <div className="flex items-center gap-1">
                  <ShoppingBag className="h-4 w-4" />
                  <User className="h-5 w-5" />
                </div>
                <span className="hidden sm:inline">Kleinanzeigen</span>
                <ExternalLink className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" />
              </Button>
            </a>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-16 left-0 right-0 bg-gray-900 border-t border-cyan-500/30 shadow-2xl z-40">
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Navigation Links */}
              <a 
                href="https://www.instagram.com/floriamalerfestpreis/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-300 hover:text-pink-400 py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20 hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Instagram className="h-5 w-5" />
                <span>Instagram</span>
              </a>
              
              <a 
                href="/bewertungen" 
                className="flex items-center gap-3 text-gray-300 hover:text-yellow-400 py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/20 hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Star className="h-5 w-5" />
                <span>Bewertungen</span>
              </a>
              
              <a 
                href="#kontakt" 
                className="flex items-center gap-3 text-gray-300 hover:text-green-400 py-3 px-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20 hover:bg-gray-800"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span>📞</span>
                <span>Kontakt</span>
              </a>

              {/* Mobile CTA Buttons */}
              <div className="pt-4 border-t border-gray-700 space-y-3">
                <a
                  href="https://handwerk.check24.de/craftsmen/cs/profile/sp_oqNze"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold transition-all duration-300 hover:from-blue-700 hover:to-blue-800"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  CHECK24 Profi Profil
                </a>
                
                <a
                  href="https://www.kleinanzeigen.de/pro/maler-mito-aldingen?utm_source=mail&utm_medium=social&utm_campaign=socialbuttons&utm_content=mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white rounded-lg font-semibold transition-all duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <ShoppingBag className="h-4 w-4" />
                  <span>Kleinanzeigen</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;